/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import replayImage from "../../assets/icons/retry.svg";
import { ACTION_TYPE } from "../../core/redux/actionType";
import { ApiError } from "../../core/redux/api/apiError";
import { clearApiErrorAction } from "../../core/redux/api/clearApiErrorAction";
import { getError, getLoading } from "../../core/redux/api/module";
import { IAppState } from "../../core/redux/IAppState";
import { getResource } from "../../language/resources/getResource";
import ErrorCountSummary from "../../layout/ErrorCountSummary";
import { showSnackbarAction } from "../../snackbar/snackbarAction";
import { FADE_IN_KEYFRAMES } from "../../styles/animations";
import {
  BLUE_CREEK,
  COOL_GREY,
  DARK_BLUE_CREEK,
  WHITE,
} from "../../styles/colors";
import { MediumText } from "../../styles/styles";
import { replayGuestHydrationErrorsAction } from "./replayGuestHydrationErrorsAction";
import { css } from "@emotion/react";
import { getGuestHydrationErrorsCountAction } from "./getGuestHydrationErrorsCountAction";


const replayImageStyle = css`
  width: 15px;
  margin-right: 5px;
  pointer-events: initial;
  margin-bottom: -3px; /* align image with text */
`;

const replayButton = css`
  text-align: center;
  text-decoration: none;
  ${MediumText}
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${BLUE_CREEK};
  color: ${WHITE};
  border: none;
  border-radius: 0.1rem;

  width: 115px;
  min-height: 35px;
  vertical-align: middle;
  position: relative;
  left: 25%;

  &:hover {
    background-color: ${DARK_BLUE_CREEK};
  }

  &:disabled {
    background-color: ${COOL_GREY};
    cursor: not-allowed !important;
  }
`;

const replayButtonContainer = css`
  display: grid;
  grid-template-columns: auto 50px;
  padding-top: 5px;
  width: fit-content;
`;

const errorCountContainer = css`
  display: grid;
  position: relative;
  right: 21%;
  top: -15px;
  width: fit-content;
  scale: 0.65;
  z-index: 1;
`;

const replayTextPosition = css`
  margin-right: 25%;
`;

type Props = {
  masterGuestId: string;
  errorCount: number;
};

/**
 * Hydration Error Retry Button for retrying guest hydration errors
 */
export const GuestHydrationErrorReplayButton = (props: Props) => {
  const [replayRequestId, setReplayRequestId] = useState<string | undefined>(
    undefined
  );
  const [enableReplay, setEnableReplay] = useState<boolean>(true);

  const { isReplaying, replayError, resortAbbrev, resources } = useSelector(
    (state: IAppState) => ({
      isReplaying: !!getLoading(
        state,
        replayRequestId!,
        ACTION_TYPE.REPLAY_GUEST_HYDRATION_ERRORS_REQUEST
      ),
      replayError: getError(
        state,
        replayRequestId!,
        ACTION_TYPE.REPLAY_GUEST_HYDRATION_ERRORS_REQUEST
      ),
      resortAbbrev: state.selectedResort.resortAbbreviation,
      resources: state.locale,
    })
  );

  const [replayButtonText, setReplayButtonText] = useState<string>(
    getResource(resources, "GUEST_HYDRATION_ERRORS_RETRY")
  );

  const dispatch = useDispatch();

  const showSnackbar = useCallback((message: string) =>
    dispatch(showSnackbarAction(message)), [dispatch]);
  const clearError = useCallback((error: ApiError) =>
    dispatch(clearApiErrorAction(error.requestId, error.actionType)), [dispatch]);
  const showError = useCallback((errorMsg: string) =>
    dispatch(showSnackbarAction(errorMsg, true)), [dispatch]);

  useEffect(() => {
    setReplayButtonText(getResource(resources, "GUEST_HYDRATION_ERRORS_RETRY"));
    setEnableReplay(true);
    if (props.errorCount > 0) {
      setReplayRequestId(`${resortAbbrev}_${props.masterGuestId}`);
    } else {
      setReplayRequestId(undefined);
    }
  }, [props.errorCount, props.masterGuestId, resortAbbrev, resources]);

  // A row is one error, so every row of a guest with several errors shares this button's request id, and the
  // replay endpoint is per guest. Only the button that started a replay reports how it went, or each of them
  // announces every replay of that guest. 'requested' also covers the microtask before the middleware
  // registers the request, when "not replaying" would otherwise read as finished.
  const replay = useRef<"idle" | "requested" | "inFlight">("idle");

  useEffect(() => {
    if (replay.current === "idle") return;

    if (replayError) {
      replay.current = "idle";
      clearError(replayError);
      showError(
        getResource(
          resources,
          "GUEST_HYDRATION_ERRORS_REPLAY_FAILURE",
          props.masterGuestId,
          resortAbbrev
        )
      );
      setReplayButtonText(
        getResource(resources, "GUEST_HYDRATION_ERRORS_RETRY")
      );
      setEnableReplay(true);
      return;
    }

    if (isReplaying) {
      replay.current = "inFlight";
      return;
    }

    if (replay.current === "inFlight") {
      replay.current = "idle";
      showSnackbar(
        getResource(resources, "GUEST_HYDRATION_ERRORS_REPLAY_SUCCESS")
      );
    }
  }, [clearError, isReplaying, props.masterGuestId, replayError, resortAbbrev, resources, showError, showSnackbar]);

  const onReplayClick = () => {
    replay.current = "requested";
    setEnableReplay(false);
    setReplayButtonText(
      getResource(resources, "GUEST_HYDRATION_ERRORS_RETRIED")
    );
    dispatch(
      replayGuestHydrationErrorsAction(resortAbbrev, props.masterGuestId)
    );
    dispatch(
      getGuestHydrationErrorsCountAction(resortAbbrev)
    );
  };

  return (
    <span css={replayButtonContainer}>
      <button
        type="button"
        disabled={!enableReplay}
        onClick={onReplayClick}
        css={replayButton}
      >
        <img
          css={replayImageStyle}
          src={replayImage}
          alt=""
          title={getResource(resources, "GUEST_HYDRATION_ERRORS_RETRY")}
        />
        <span css={replayTextPosition}>{replayButtonText}</span>
      </button>
      {enableReplay && (
        <span css={errorCountContainer}>
          <ErrorCountSummary value={props.errorCount ?? 0}></ErrorCountSummary>
        </span>
      )}
    </span>
  );
};
